import { useState, useRef, useCallback } from 'react';
import Webcam from 'react-webcam';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Camera, X, SwitchCamera, Check, RotateCcw } from 'lucide-react';

export function ImageCapture({ onCapture, onClose, isOpen }) {
    const [facingMode, setFacingMode] = useState('environment');
    const [capturedImage, setCapturedImage] = useState(null);
    const [error, setError] = useState(null);
    const webcamRef = useRef(null);

    const capture = useCallback(() => {
        if (webcamRef.current) {
            const imageSrc = webcamRef.current.getScreenshot();
            setCapturedImage(imageSrc);
        }
    }, [webcamRef]);

    const retake = () => {
        setCapturedImage(null);
    };

    const confirm = () => {
        onCapture(capturedImage);
        setCapturedImage(null);
    };

    const switchCamera = () => {
        setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
    };

    const handleError = (err) => {
        console.error('Webcam error:', err);
        setError('Camera access denied. Please allow camera permissions.');
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 bg-black/90 flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 text-white">
                <h2 className="text-lg font-semibold">Take Product Photo</h2>
                <div className="flex gap-2">
                    <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={switchCamera}
                        className="text-white hover:bg-white/20"
                    >
                        <SwitchCamera className="h-5 w-5" />
                    </Button>
                    <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={onClose}
                        className="text-white hover:bg-white/20"
                    >
                        <X className="h-5 w-5" />
                    </Button>
                </div>
            </div>

            {/* Camera/Preview Area */}
            <div className="flex-1 flex items-center justify-center p-4">
                <div className="relative w-full max-w-md aspect-square">
                    {error ? (
                        <Card className="bg-red-500/20 border-red-500 h-full flex items-center justify-center">
                            <CardContent className="text-center text-white">
                                <Camera className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                <p>{error}</p>
                            </CardContent>
                        </Card>
                    ) : capturedImage ? (
                        <img 
                            src={capturedImage} 
                            alt="Captured" 
                            className="w-full h-full object-cover rounded-lg"
                        />
                    ) : (
                        <Webcam
                            ref={webcamRef}
                            audio={false}
                            screenshotFormat="image/jpeg"
                            videoConstraints={{
                                facingMode: facingMode,
                                width: 720,
                                height: 720
                            }}
                            onUserMediaError={handleError}
                            className="w-full h-full object-cover rounded-lg"
                        />
                    )}
                </div>
            </div>

            {/* Controls */}
            <div className="p-4 flex justify-center gap-4">
                {capturedImage ? (
                    <>
                        <Button 
                            variant="outline" 
                            size="lg"
                            onClick={retake}
                            className="text-white border-white hover:bg-white/20"
                        >
                            <RotateCcw className="h-5 w-5 mr-2" />
                            Retake
                        </Button>
                        <Button 
                            size="lg"
                            onClick={confirm}
                            className="bg-emerald-600 hover:bg-emerald-700"
                        >
                            <Check className="h-5 w-5 mr-2" />
                            Use Photo
                        </Button>
                    </>
                ) : (
                    <Button 
                        size="lg"
                        onClick={capture}
                        className="bg-white text-black hover:bg-gray-200 rounded-full w-16 h-16"
                    >
                        <Camera className="h-8 w-8" />
                    </Button>
                )}
            </div>
        </div>
    );
}
